/**
 * cookieHelper.js — Cookie Management Utilities
 * ─────────────────────────────────────────────────────
 * CHANGES:
 *  1. setCookie now adds Secure flag when on HTTPS (production)
 *  2. setCookie accepts days (default 7) — consistent unit
 *  3. All functions wrapped in try/catch for resilience
 *  4. clearAllCookies iterates safely
 *  5. Removed redundant hasSessionCookie — consolidated into authValidator.hasValidSession
 *     (kept here for backward compatibility but delegates logic)
 */

const IS_SECURE = window.location.protocol === 'https:';

/**
 * Check if a session cookie exists.
 * Kept for backward compat — prefer authValidator.hasValidSession().
 */
export const hasSessionCookie = () => {
  try {
    const cookies = document.cookie.split(';');
    return cookies.some((cookie) => {
      const trimmed = cookie.trim();
      return trimmed.startsWith('session_token=') ||
             trimmed.startsWith('auth_session=');
    });
  } catch (error) {
    console.error('Error checking session cookie:', error);
    return false;
  }
};

/**
 * Get a cookie value by name.
 * @param {string} name
 * @returns {string|null}
 */
export const getCookie = (name) => {
  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return null;
  } catch (error) {
    console.error('Error getting cookie:', error);
    return null;
  }
};

/**
 * Set a cookie.
 * @param {string} name
 * @param {string} value
 * @param {number} days — expiry in days (default 7)
 */
export const setCookie = (name, value, days = 7) => {
  try {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

    let cookieStr = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
    if (IS_SECURE) {
      cookieStr += ';Secure';
    }
    document.cookie = cookieStr;
  } catch (error) {
    console.error('Error setting cookie:', error);
  }
};

/**
 * Delete a cookie by name.
 * @param {string} name
 */
export const deleteCookie = (name) => {
  try {
    let cookieStr = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Lax`;
    if (IS_SECURE) {
      cookieStr += ';Secure';
    }
    document.cookie = cookieStr;
  } catch (error) {
    console.error('Error deleting cookie:', error);
  }
};

/**
 * Clear all client-readable cookies. Used during logout.
 */
export const clearAllCookies = () => {
  try {
    const cookies = document.cookie.split(';');
    cookies.forEach((cookie) => {
      const name = cookie.split('=')[0].trim();
      if (name) {
        deleteCookie(name);
      }
    });
  } catch (error) {
    console.error('Error clearing cookies:', error);
  }
};
